import { useEffect, useMemo, useRef, useState } from 'react';

export type MergeFilesFn = (existingFiles: File[], incomingFiles: File[]) => File[];

interface ImagesFieldProps {
  files: File[];
  onChange: (files: File[]) => void;
  mergeFiles: MergeFilesFn;
}

export const ImagesField = ({ files, onChange, mergeFiles }: ImagesFieldProps) => {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const previewItems = useMemo(
    () =>
      files.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    [files],
  );

  useEffect(() => {
    return () => {
      previewItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [previewItems]);

  return (
    <div className="w-full">
      <label htmlFor="message-image" className="mb-1 block text-sm font-medium text-gray-700">
        Изображения
      </label>
      <div
        onDrop={(event) => {
          event.preventDefault();
          setIsDraggingFiles(false);
          const droppedFiles = Array.from(event.dataTransfer.files || []);
          if (!droppedFiles.length) return;
          onChange(mergeFiles(files, droppedFiles));
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDraggingFiles(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDraggingFiles(false);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-4 transition-colors ${
          isDraggingFiles ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
      >
        <input
          id="message-image"
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(event) => {
            const selectedFiles = Array.from(event.target.files || []);
            if (!selectedFiles.length) return;
            onChange(mergeFiles(files, selectedFiles));
            event.target.value = '';
          }}
        />
        <p className="text-sm text-gray-700">Перетащите изображения сюда или нажмите для выбора</p>
        <button
          type="button"
          className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          onClick={(event) => {
            event.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Прикрепить изображения
        </button>
        <p className="mt-2 text-xs text-gray-500">Поле необязательное</p>
      </div>

      {!!previewItems.length && (
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {previewItems.map(({ file, previewUrl }, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="overflow-hidden rounded border border-gray-200"
            >
              <img src={previewUrl} alt={file.name} className="h-48 w-full object-contain" />
              <div className="flex items-center justify-between gap-2 px-2 py-2 text-xs">
                <span className="truncate" title={file.name}>
                  {file.name}
                </span>
                <button
                  type="button"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => {
                    onChange(files.filter((_file, currentIndex) => currentIndex !== index));
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
